import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Card, CardContent, Link as MuiLink } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";
import { truncate } from "bundles/common/utils/utils";
import HTML from "bundles/common/components/HTML";
import moment from "moment";

const CATEGORY_LABEL_MAP = {
  career_advise: "blog.categories.advices",
  interviews_and_cv: "blog.categories.interviews_and_cv",
  candidate_actuality: "blog.categories.actualities",
  how_to_recruit: "blog.categories.how_to_recruit",
  human_resources: "blog.categories.human_resources",
  recruiter_actuality: "blog.categories.actualities",
};

const BlogSection = ({ type = "candidate" }) => {
  const theme = useTheme();
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [articles, setArticles] = useState([]);

  const validLocales = ["fr", "nl", "en"];
  const localeFilter = validLocales.includes(i18nLocale)
    ? { preferred_locale: i18nLocale }
    : {};

  useEffect(() => {
    sh.get("search/blog_posts", {
      authenticity_token: authenticityToken,
      params: {
        page: 1,
        filters: {
          resource_type: type,
          ...localeFilter,
        },
      },
    })
      .then((res) => {
        if (res?.data) {
          const posts = res.data.data?.blog_posts || [];
          setArticles(posts.slice(0, 3));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [type, i18nLocale]);

  const postsPath = type === "recruiter" ? "recruiters" : "candidates";
  const allArticlesUrl = `/${i18nLocale}/${postsPath}/posts`;

  if (articles.length === 0) return null;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "2rem", md: "3rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ maxWidth: "72rem", mx: "auto" }}>
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "3rem" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.375rem",
              px: "1rem",
              py: "0.375rem",
              borderRadius: "9999px",
              bgcolor: "hsl(153 68% 25% / 0.1)",
              color: "hsl(153 68% 25%)",
              fontSize: "0.75rem",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              mb: "1rem",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            <BookOpen size={14} />
            <FormattedMessage id="landing_recruiter.blog_section.eyebrow" />
          </Box>

          <Box
            component="h2"
            sx={{
              m: 0,
              mb: "0.75rem",
              fontSize: { xs: "1.5rem", md: "1.875rem" },
              color: "hsl(220, 25%, 12%)",
              letterSpacing: "-0.02em",
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="landing_recruiter.blog_section.heading" />
          </Box>

          <Box
            component="p"
            sx={{
              m: 0,
              mx: "auto",
              maxWidth: "36rem",
              color: "hsl(220, 10%, 46%)",
              fontSize: "1rem",
              lineHeight: 1.6,
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id={`landing_recruiter.blog_section.subtitle.${type}`} />
          </Box>
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: "1.5rem",
            mb: "2.5rem",
          }}
        >
          {articles.map((post, index) => (
            <Box
              key={post.id}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <MuiLink
                href={`/${i18nLocale}/${postsPath}/posts/${post.encoded_title}/${post.id}`}
                underline="none"
                sx={{ display: "flex", height: "100%" }}
              >
                <Card
                  elevation={0}
                  sx={{
                    overflow: "hidden",
                    border: "1px solid hsl(220, 13%, 91%, 0.6)",
                    borderRadius: "0.75rem",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    transition: "box-shadow 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 10px 30px -5px rgba(0, 0, 0, 0.1), 0 4px 10px -5px rgba(0, 0, 0, 0.04)",
                    },
                    "&:hover .blog-card-image": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {/* Image */}
                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: "66.666%", // aspect 3/2
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="img"
                      className="blog-card-image"
                      src={post.cover_picture_url}
                      alt={post.title}
                      loading="lazy"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.5s ease",
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <CardContent
                    sx={{
                      p: "1.25rem",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      "&:last-child": { pb: "1.25rem" },
                    }}
                  >
                    {/* Category */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", mb: "0.75rem" }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: "hsl(153 68% 25%)",
                          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        }}
                      >
                        {post.category && CATEGORY_LABEL_MAP[post.category]
                          ? intl.formatMessage({ id: CATEGORY_LABEL_MAP[post.category] })
                          : post.category}
                      </Box>
                    </Box>

                    {/* Title */}
                    <Box
                      component="h3"
                      sx={{
                        m: 0,
                        mb: "0.5rem",
                        color: "hsl(220, 25%, 12%)",
                        fontSize: "0.95rem",
                        lineHeight: 1.4,
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {post.title}
                    </Box>

                    {/* Excerpt */}
                    <Box
                      sx={{
                        flex: 1,
                        color: "hsl(220, 10%, 46%)",
                        fontSize: "0.85rem",
                        lineHeight: 1.7,
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <HTML
                        html={truncate(post.content, 120)}
                        blacklistedTags={["strong", "p"]}
                      />
                    </Box>

                    {/* Date */}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "11px",
                        color: "hsl(220, 10%, 46%)",
                        mt: "0.75rem",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      {moment(post.created_at).format("DD MMMM YYYY")}
                    </Box>
                  </CardContent>
                </Card>
              </MuiLink>
            </Box>
          ))}
        </Box>

        {/* CTA Button */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center" }}
        >
          <Box
            component="a"
            href={allArticlesUrl}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              px: "1.5rem",
              py: "0.875rem",
              bgcolor: "hsl(153 68% 25%)",
              color: theme.palette.common.white,
              fontSize: "0.875rem",
              fontWeight: 700,
              borderRadius: "0.75rem",
              textDecoration: "none",
              transition: "background-color 0.2s ease",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
              },
              "& .arrow-icon": {
                transition: "transform 0.2s ease",
              },
              "&:hover .arrow-icon": {
                transform: "translateX(2px)",
              },
            }}
          >
            <FormattedMessage id="landing_recruiter.blog_section.cta" />
            <ArrowRight size={14} className="arrow-icon" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BlogSection;
